import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBars,
  FaCheck,
  FaGlobe,
  FaLayerGroup,
  FaMagnifyingGlass,
  FaPalette,
  FaPenRuler,
  FaRocket,
  FaStar,
} from "react-icons/fa6";
import Footer from "./Footer";
import "./CSS/home.css";
import "./CSS/homepage-builder.css";

const steps = [
  {
    number: "01",
    title: "Make it yours",
    copy: "Add your brand, services, photos and contact details.",
    icon: <FaPenRuler />,
  },
  {
    number: "02",
    title: "Shape the experience",
    copy: "Choose a look that feels like your business — no code required.",
    icon: <FaPalette />,
  },
  {
    number: "03",
    title: "Go live and get found",
    copy: "Share a polished site and appear in the CircleGrid directory.",
    icon: <FaRocket />,
  },
];

const CustomerHomePage = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="builder-home">
      <Helmet>
        <title>CircleGrid — Build your business online</title>
        <meta
          name="description"
          content="Build a beautiful business website, get discovered in the CircleGrid directory, and turn visits into enquiries."
        />
        <meta property="og:title" content="CircleGrid — Build your business online" />
        <meta
          property="og:description"
          content="Your website, local visibility and business growth tools in one place."
        />
      </Helmet>

      <header className="builder-nav-shell">
        <nav className="builder-nav" aria-label="Main navigation">
          <Link className="builder-brand" to="/" aria-label="CircleGrid home">
            <span className="builder-brand-mark"><i /></span>
            CircleGrid
          </Link>

          <div className="builder-nav-links">
            <a href="#how-it-works">How it works</a>
            <a href="#directory">Directory</a>
            <Link to="/buyers-portal">Find services</Link>
            <Link to="/Pricing">Pricing</Link>
            <Link to="/About">About</Link>
          </div>

          <div className="builder-nav-actions">
            <Link className="builder-login" to="/Login">Log in</Link>
            <Link className="builder-button builder-button-small" to="/Signup">
              Start building <FaArrowRight />
            </Link>
          </div>

          <button
            className="builder-menu"
            type="button"
            aria-label="Open navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <FaBars />
          </button>
        </nav>
        <div className={`builder-mobile-nav ${menuOpen ? "open" : ""}`}>
          <a href="#how-it-works" onClick={() => setMenuOpen(false)}>How it works</a>
          <a href="#directory" onClick={() => setMenuOpen(false)}>Directory</a>
          <Link to="/buyers-portal" onClick={() => setMenuOpen(false)}>Find services</Link>
          <Link to="/Pricing" onClick={() => setMenuOpen(false)}>Pricing</Link>
          <Link to="/About" onClick={() => setMenuOpen(false)}>About</Link>
          <Link to="/Login" onClick={() => setMenuOpen(false)}>Log in</Link>
          <Link className="builder-button" to="/Signup" onClick={() => setMenuOpen(false)}>Start building <FaArrowRight /></Link>
        </div>
      </header>

      <main>
        <section className="builder-hero">
          <span className="builder-orb builder-orb-one" />
          <span className="builder-orb builder-orb-two" />

          <div className="builder-hero-grid">
            <div className="builder-hero-copy">
              <p className="builder-kicker"><span /> THE HOME OF GROWING LOCAL BUSINESSES</p>
              <h1>Build a website that <em>works</em> as hard as you do.</h1>
              <p className="builder-hero-lede">
                Create a beautiful online home for your business, then get in front of local
                customers who are ready to connect.
              </p>
              <div className="builder-hero-actions">
                <Link className="builder-button" to="/Signup">
                  Create your website <FaArrowRight />
                </Link>
                <a
                  className="builder-text-link"
                  href="https://circlegrid.org/site/chocolatetour"
                  target="_blank"
                  rel="noreferrer"
                >
                  View a sample website <span>↗</span>
                </a>
              </div>
              <div className="builder-proof">
                <div className="builder-avatars"><i>J</i><i>M</i><i>S</i><i>+</i></div>
                <span>Built for ambitious local businesses</span>
              </div>
            </div>

            <div className="builder-window" aria-label="CircleGrid website builder preview">
              <div className="builder-browser-bar">
                <div><i /><i /><i /></div>
                <span>circlegrid.co/your-business</span>
                <b>↗</b>
              </div>
              <div className="builder-interface">
                <aside>
                  <strong><i className="builder-mini-logo" /> Studio</strong>
                  <button className="active"><FaPenRuler /> Design</button>
                  <button><FaLayerGroup /> Pages</button>
                  <button><FaPalette /> Style</button>
                  <button><FaGlobe /> Publish</button>
                  <small>● Saved</small>
                </aside>

                <div className="builder-site-canvas">
                  <div className="builder-site-nav">
                    <b>Oak &amp; Ember</b>
                    <span>About &nbsp; Services &nbsp; Contact</span>
                    <i>Book now</i>
                  </div>
                  <div className="builder-site-hero">
                    <p>INTERIORS THAT FEEL LIKE HOME</p>
                    <h2>Spaces with<br /><em>soul.</em></h2>
                    <span>Explore our work <FaArrowRight /></span>
                  </div>
                  <div className="builder-site-cards"><span /><span /><span /></div>
                </div>

                <div className="builder-style-panel">
                  <p>STYLE</p>
                  <b>Warm Minimal</b>
                  <div><i /><i /><i /><i /></div>
                  <small>Typography</small>
                  <strong>DM Sans <span>⌄</span></strong>
                </div>
              </div>
              <div className="builder-cursor">✦ <span>Looking good!</span></div>
            </div>
          </div>
        </section>

        <section className="builder-value-strip">
          <p>Everything you need to get your business <strong>online, visible and growing.</strong></p>
          <div>
            <span><FaPenRuler /> Easy to build</span>
            <span><FaGlobe /> Ready to share</span>
            <span><FaRocket /> Made to grow</span>
          </div>
        </section>

        <section id="how-it-works" className="builder-steps-section">
          <div className="builder-section-heading">
            <p className="builder-kicker builder-kicker-slate"><span /> SIMPLE BY DESIGN</p>
            <h2>From idea to online<br />in a few good moves.</h2>
            <p>CircleGrid gives you the tools to show up professionally — without the tech headache.</p>
          </div>
          <div className="builder-steps">
            {steps.map((step, index) => (
              <article className={`builder-step builder-step-${index + 1}`} key={step.number}>
                <span className="builder-step-number">{step.number}</span>
                <div className="builder-step-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
                <span className="builder-step-line" />
              </article>
            ))}
          </div>
        </section>

        <section className="builder-feature">
          <div className="builder-feature-copy">
            <p className="builder-kicker builder-kicker-slate"><span /> NO CODE. NO COMPROMISE.</p>
            <h2>Your business has personality.<br /><em>Your website should too.</em></h2>
            <p>
              Start with a great foundation, then make every detail feel unmistakably yours.
              Your CircleGrid site is designed to look good on every screen from day one.
            </p>
            <ul>
              <li><FaCheck /> Build with confidence</li>
              <li><FaCheck /> Keep your content up to date</li>
              <li><FaCheck /> Share a site you’re proud of</li>
            </ul>
            <Link className="builder-text-link builder-orange-link" to="/Signup">
              Start your website <FaArrowRight />
            </Link>
          </div>

          <div className="builder-phone-scene">
            <div className="builder-phone">
              <div className="builder-phone-notch" />
              <div className="builder-phone-page">
                <small>OAK &amp; EMBER <span>☰</span></small>
                <div className="builder-phone-image" />
                <p>MADE FOR LIVING</p>
                <h3>Interiors with<br /><em>intention.</em></h3>
                <button>View our work</button>
              </div>
            </div>
            <div className="builder-floating-card">
              <FaStar />
              <b>Looks great everywhere</b>
              <span>Mobile-ready by default</span>
            </div>
          </div>
        </section>

        <section id="directory" className="builder-directory">
          <div className="builder-directory-copy">
            <p className="builder-kicker"><span /> BUILT-IN DISCOVERY</p>
            <h2>Don’t just build a site.<br />Build your <em>visibility.</em></h2>
            <p>
              CircleGrid began as a local business directory. Now, your website and your
              listing work together to help the right customers find you.
            </p>
            <div className="builder-directory-points">
              <span><FaMagnifyingGlass /> Be discovered locally</span>
              <span><FaStar /> Build trust with your profile</span>
            </div>
            <Link className="builder-button builder-light-button" to="/Directory">
              Explore the directory <FaArrowRight />
            </Link>
          </div>

          <div className="builder-search-card">
            <p>FIND A LOCAL BUSINESS</p>
            <div>
              <FaMagnifyingGlass />
              <span>What are you looking for?</span>
              <Link to="/Directory">Search</Link>
            </div>
            <small><i /> Verified local businesses, all in one place</small>
          </div>
        </section>

        <section className="builder-final-cta">
          <p className="builder-kicker builder-kicker-slate"><span /> YOUR NEXT CHAPTER STARTS HERE</p>
          <h2>Show the world<br />what you’re building.</h2>
          <p>Make your business easier to find, easier to trust and impossible to forget.</p>
          <Link className="builder-button" to="/Signup">Build your website <FaArrowRight /></Link>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CustomerHomePage;
